import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useSearchParams, useLocation } from "react-router-dom";
import useThemeStore from "@/shared/stores/useThemeStore";
import { useAuth } from "@/shared/stores/useAuth";

import { useLaunchStore } from "@/shared/components/layout/useLaunchStore";
import {
  DECK_CARD_REDIRECT_URL_KEY,
  isPublicDeckCardShareLocation,
  prepareDeckCardPostLoginRedirect,
  setDeckCardRedirect,
} from "./utils/deckCardShareRedirect";
import DeckCardLibrary from "./component/deck-library";

export type DeckFilter = "Single_Card" | "Demo_Card" | "Listening_Together_Card" | "Season_Card";

export const SingleAppDeck: React.FC = () => (
  <DeckCardLibrary
    cardType="Single_Card"
    title="Single Album Cards"
    subtitle="Single album cards — Listen to your favorite songs with favourite artists."
  />
);

export const DemoAppDeck: React.FC = () => (
  <DeckCardLibrary
    cardType="Demo_Card"
    title="Demo Cards"
    subtitle="Preview talent demos — discover samples and book a full experience."
  />
);

export const ListeningTogetherAppDeck: React.FC = () => (
  <DeckCardLibrary
    cardType="Listening_Together_Card"
    title="Listening Together Cards"
    subtitle="Shared listening sessions — experience new music live with your favorite talent."
  />
);

export const SeasonAppDeck: React.FC = () => (
  <DeckCardLibrary
    cardType="Season_Card"
    title="Season Cards"
    subtitle="Episodic story arcs fans unlock chapter by chapter."
  />
);

const DECK_BY_FILTER: Record<DeckFilter, React.FC> = {
  Single_Card: SingleAppDeck,
  Demo_Card: DemoAppDeck,
  Listening_Together_Card: ListeningTogetherAppDeck,
  Season_Card: SeasonAppDeck,
};

const DEFAULT_FILTER: DeckFilter = "Single_Card";

const TaasCardsDeck: React.FC = () => {
  const { darkMode } = useThemeStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const clearLaunch = useLaunchStore((s) => s.clear);
  const { userInfo } = useAuth();

  useEffect(() => {
    if (!isPublicDeckCardShareLocation(location.pathname, location.search)) {
      return;
    }

    const currentUrl = window.location.href;

    if (!userInfo?.id) {
      setDeckCardRedirect(currentUrl);
      return;
    }

    const storedRedirectUrl = localStorage.getItem(DECK_CARD_REDIRECT_URL_KEY);
    if (storedRedirectUrl && storedRedirectUrl !== currentUrl) {
      prepareDeckCardPostLoginRedirect();
      window.location.href = storedRedirectUrl;
    }
  }, [location.pathname, location.search, userInfo?.id]);

  const arrivedViaLaunch = !!(location.state as any)?.launchGradient;
  const [contentVisible, setContentVisible] = useState(!arrivedViaLaunch);

  useEffect(() => {
    if (!arrivedViaLaunch) return;
    const t1 = setTimeout(() => setContentVisible(true), 200);
    const t2 = setTimeout(() => clearLaunch(), 700);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filterParam = searchParams.get("filter") as DeckFilter | null;
  const activeFilter = filterParam && filterParam in DECK_BY_FILTER ? filterParam : DEFAULT_FILTER;
  const Component = DECK_BY_FILTER[activeFilter];

  useEffect(() => {
    if (filterParam && filterParam in DECK_BY_FILTER) return;
    const next = new URLSearchParams(searchParams);
    next.set("filter", DEFAULT_FILTER);
    setSearchParams(next, { replace: true });
  }, [filterParam, searchParams, setSearchParams]);

  const pageBg = darkMode ? "var(--neutral-950, #0a0a0f)" : "#f8f9fb";

  return (
    <Box sx={{ minHeight: "100vh", background: pageBg, pb: 6 }}>
      <Box
        sx={{
          opacity: contentVisible ? 1 : 0,
          transform: contentVisible ? "translateY(0) scale(1)" : "translateY(22px) scale(0.97)",
          transition: contentVisible
            ? "opacity 0.40s cubic-bezier(0.32,0.72,0,1), transform 0.40s cubic-bezier(0.32,0.72,0,1)"
            : "none",
          pt: 1,
        }}
      >
        <Component />
      </Box>
    </Box>
  );
};

export default TaasCardsDeck;
